import { useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { initialize as formInitialize, reduxForm } from 'redux-form';
import { styled } from '@mui/system';

// Libs
import { GridContainer, GridItem, Input } from '@formComponents';
import { Button } from '@libComponents';
import ContentContainer from '@libComponents/ContentContainer';
import { hasFormErrorSelector } from 'common/redux.selectors';
import { getWeekDays } from '@libs/dateTime';
import AutocompleteBase from '@formComponents/Autocomplete';

import { FORM_NAME, formatedShiftData } from './common';
import {
    getInitialShift,
    updateManageShifts
} from 'employee/ManageEmployee/redux.actions';
import { SHIFT } from 'common/redux.datasets';
import { fetchDatasets } from '@libs/datasets';
import { getAllShift } from 'common/components/shift/redux.selectors';
import Shift from 'common/components/shift';

const propTypes = {
    mode: PropTypes.oneOf(['create', 'update'])
};
const defaultProps = {
    mode: 'create'
};

/**
 * ReduxForm stepper rules
 * * Connect each step with reduxForm() to the same form name.
 * * Specify the destroyOnUnmount: false flag to preserve form data across form component unmounts.
 */
const ButtonWrapper = styled('div')({
    position: 'absolute',
    bottom: 0,
    right: '50%',
    transform: 'translate(50%, 50%)',
    zIndex: 4
});

const StyledButton = styled(Button)(({ theme: { spacing, typography } }) => ({
    height: spacing(4.75),
    width: spacing(25),
    fontSize: typography.subtitle1.fontSize,
    marginLeft: spacing(0.75),
    marginRight: spacing(0.75)
}));

const ManageShifts = reduxForm({
    form: FORM_NAME, // Form name
    destroyOnUnmount: false, // Preserve data
    onSubmit: (v) => v
})(({ mode, handleSubmit: handleReduxFormSubmit, ...rest }) => {
    const { state } = useLocation();
    const RXDispatch = useDispatch();

    const { email } = state ? state : '';
    const hasError = useSelector((state) =>
        hasFormErrorSelector(state, FORM_NAME)
    );

    const shifts = useSelector((state) => getAllShift(state));

    const handleGetManageShifts = useCallback(
        ({ enhancedEmployeeShiftData }) => {
            RXDispatch(formInitialize(FORM_NAME, enhancedEmployeeShiftData));
        },
        [RXDispatch]
    );

    useEffect(() => {
        if (email) {
            RXDispatch(fetchDatasets(SHIFT));
            RXDispatch(
                getInitialShift({
                    handleSubmit: handleGetManageShifts,
                    payload: { employeeEmail: email }
                })
            );
            RXDispatch(formInitialize(FORM_NAME, { employeeEmail: email }));
        }
    }, [email, RXDispatch, handleGetManageShifts]);

    const handleManage = () => {
        if (hasError) {
            handleReduxFormSubmit();
            return;
        }
        RXDispatch(updateManageShifts({ handleSubmit }));
    };

    const handleSubmit = (payload) => {
        // console.log(payload);
    };

    let weekdays = getWeekDays();

    return (
        <div style={{ padding: '16px' }}>
            <ContentContainer
                title='Employee Email'
                AdornmentComponent={<Fragment />}
                sx={{ display: 'none' }}
            >
                <GridContainer>
                    <GridItem md={4}>
                        <Input
                            name='employeeEmail'
                            placeholder='Employee Email'
                            label='Employee E-Mail'
                            disabled={true}
                            autoComplete='do-not-autofill'
                        />
                    </GridItem>
                </GridContainer>
            </ContentContainer>

            <ContentContainer
                title='Weekly Shift Schedule'
                AdornmentComponent={<Fragment />}
            >
                <GridContainer>
                    {weekdays.map(({ label, value }, i) => (
                        <GridItem md={3} key={label}>
                            <AutocompleteBase
                                name={`shifts${value}`}
                                placeholder={`Select ${label} Shift`}
                                label={label}
                                options={formatedShiftData(shifts)}
                                ContainerComponent={Shift}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
                <ButtonWrapper>
                    <StyledButton onClick={handleManage}>UPDATE</StyledButton>
                </ButtonWrapper>
            </ContentContainer>
        </div>
    );
});

ManageShifts.propTypes = propTypes;
ManageShifts.defaultProps = defaultProps;

export { ManageShifts as default, ManageShifts };
